import React, { Component } from 'react';
import { ImageInput } from '@plone/volto/components/manage/Widgets/ImageWidget';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import { EditorReference, SlateEditor } from '@plone/volto-slate/editor';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { injectIntl } from 'react-intl';
import doesNodeContainClick from 'semantic-ui-react/dist/commonjs/lib/doesNodeContainClick';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { normalizeTableValue } from './imageUtils';
import {
  parseSpreadsheetClipboard,
  readRichClipboardPayload,
} from './tablePaste';

class Cell extends Component {
  static propTypes = {
    onSelectCell: PropTypes.func.isRequired,
    row: PropTypes.number,
    cell: PropTypes.number,
    value: PropTypes.array,
    selected: PropTypes.bool,
    selectedCell: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onPasteTable: PropTypes.func,
    isTableBlockSelected: PropTypes.bool,
    tableBlockId: PropTypes.string,
    imagePickerRequest: PropTypes.object,
    intl: PropTypes.objectOf(PropTypes.any),
  };

  static defaultProps = {
    value: [{ type: 'p', children: [{ text: '' }] }],
  };

  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.handleContainerFocus = this.handleContainerFocus.bind(this);
    this.openImagePicker = this.openImagePicker.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleImageChange = this.handleImageChange.bind(this);
    this.handlePasteCapture = this.handlePasteCapture.bind(this);
    this.finishTablePaste = this.finishTablePaste.bind(this);
    this.state = { editor: null, addImageOpened: false, popoverStyle: null };
    this.tableblockExtensions = config.settings.slate.tableblockExtensions;
    this.imagePickerRef = React.createRef();
    this.cellRef = React.createRef();
    this.popoverRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    document.removeEventListener('mousedown', this.handleClickOutside, false);
  }

  componentDidUpdate(prevProps) {
    const imagePickerRequest = this.props.imagePickerRequest;
    if (
      imagePickerRequest &&
      imagePickerRequest !== prevProps.imagePickerRequest &&
      imagePickerRequest.row === this.props.row &&
      imagePickerRequest.cell === this.props.cell
    ) {
      this.openImagePicker();
    }

    if (
      prevProps.isTableBlockSelected !== this.props.isTableBlockSelected &&
      this.props.isTableBlockSelected &&
      this.props.cell === 0 &&
      this.props.row === 0 &&
      (!this.props.selectedCell ||
        (this.props.selectedCell.row === 0 &&
          this.props.selectedCell.cell === 0))
    ) {
      this.props.onSelectCell(this.props.row, this.props.cell);

      if (this.state.editor) {
        setTimeout(
          () => !this.isUnmounted && ReactEditor.focus(this.state.editor),
          0,
        );
      }
    }
  }

  onChange(value) {
    this.props.onChange(this.props.row, this.props.cell, [...value]);
  }

  finishTablePaste(payload, result) {
    if (!this.isUnmounted && result.handled) {
      this.props.onPasteTable?.(
        this.props.row,
        this.props.cell,
        result,
        payload,
      );
    }
  }

  handlePasteCapture(event) {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const types = Array.from(clipboardData.types || []);
    const formats = {};
    types
      .filter((type) => type.startsWith('text/'))
      .forEach((type) => {
        formats[type] = clipboardData.getData(type);
      });
    const payload = {
      types,
      html: formats['text/html'] || '',
      text: formats['text/plain'] || '',
      formats,
    };
    const result = parseSpreadsheetClipboard(payload);
    if (!result.handled) return;

    event.preventDefault();
    event.stopPropagation();

    if (
      !payload.html &&
      typeof navigator !== 'undefined' &&
      navigator.clipboard?.read
    ) {
      readRichClipboardPayload(payload, navigator.clipboard).then(
        (richPayload) => {
          const richResult = parseSpreadsheetClipboard(richPayload);
          this.finishTablePaste(
            richPayload,
            richResult.handled ? richResult : result,
          );
        },
      );
      return;
    }

    this.finishTablePaste(payload, result);
  }

  handleContainerFocus() {
    this.props.onSelectCell(this.props.row, this.props.cell);
  }

  openImagePicker() {
    const rect = this.cellRef.current?.getBoundingClientRect();
    if (!rect) {
      this.setState({ addImageOpened: true, popoverStyle: null });
      return;
    }

    const popoverHeight = 340;
    const openUpward =
      window.innerHeight - rect.bottom < popoverHeight &&
      rect.top > popoverHeight;

    this.setState({
      addImageOpened: true,
      popoverStyle: {
        position: 'fixed',
        left: Math.min(rect.left, window.innerWidth - 280),
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      },
    });
  }

  handleClickOutside(e) {
    if (
      this.state.addImageOpened &&
      !e.target.closest?.('.table-cell-image-input') &&
      !(
        this.imagePickerRef.current &&
        doesNodeContainClick(this.imagePickerRef.current, e)
      ) &&
      !(
        this.popoverRef.current &&
        doesNodeContainClick(this.popoverRef.current, e)
      ) &&
      // Clique dentro do ObjectBrowser (renderizado em .sidebar-container via
      // portal) nao deve fechar nosso popover, senao a selecao de imagem quebra
      !e.target.closest?.('.sidebar-container') &&
      // O modal "Preparar Imagem para Upload" (semantic-ui Modal) tambem e
      // renderizado via portal direto em document.body, fora do popover
      !e.target.closest?.('.image-upload-modal') &&
      !e.target.closest?.('.modals.dimmer')
    ) {
      this.setState({ addImageOpened: false });
    }
  }

  handleImageChange(_id, image) {
    const rawUrl = Array.isArray(image)
      ? image?.[0]?.['@id']
      : image
        ? image['@id'] || image
        : '';
    if (!rawUrl) return;

    // A URL crua do objeto de conteudo (ex.: /pt-br/portal.png) retorna a
    // pagina HTML do item, nao os bytes da imagem: e preciso usar a view
    // @@images/image (sem scale) para obter a imagem no tamanho original,
    // do mesmo jeito que o bloco de imagem nativo do Volto faz.
    const normalizedUrl = flattenToAppURL(rawUrl);
    const sameOrigin =
      typeof window !== 'undefined' &&
      new URL(rawUrl, window.location.origin).origin === window.location.origin;
    const url =
      isInternalURL(rawUrl) || sameOrigin
        ? `${normalizedUrl}/@@images/image`
        : rawUrl;

    const editor = this.state.editor;
    const imgNode = {
      type: 'img',
      url,
      children: [{ text: '' }],
    };

    // Insere via Transforms (nao via prop `value`): a SlateEditor so propaga
    // para a UI mudancas feitas atraves de operacoes reais do editor, uma
    // mutacao externa do array `value` fica invisivel/e perdida na proxima edicao.
    if (editor) {
      Transforms.insertNodes(editor, imgNode, { at: Editor.end(editor, []) });
    }

    this.setState({ addImageOpened: false });
  }

  render() {
    const cellId = `${this.props.tableBlockId}-cell-${this.props.row}-${this.props.cell}`;

    return (
      __CLIENT__ && (
        <div
          className="slate-table-cell-inner"
          ref={this.cellRef}
          onPasteCapture={this.handlePasteCapture}
        >
          <SlateEditor
            tabIndex={0}
            onChange={this.onChange}
            extensions={this.tableblockExtensions}
            value={normalizeTableValue(this.props.value)}
            selected={this.props.selected}
            onFocus={this.handleContainerFocus}
            onClick={this.handleContainerFocus}
            debug={false}
          >
            <EditorReference
              onHasEditor={(editor) =>
                !this.state.editor && this.setState({ editor })
              }
            />
          </SlateEditor>
          {this.state.addImageOpened &&
            ReactDOM.createPortal(
              <div
                className="table-cell-image-input"
                style={this.state.popoverStyle}
                ref={(node) => {
                  this.imagePickerRef.current = node;
                }}
              >
                <ImageInput
                  id={cellId}
                  onChange={this.handleImageChange}
                  objectBrowserPickerType="image"
                />
              </div>,
              document.body,
            )}
        </div>
      )
    );
  }
}

export default injectIntl(Cell);
